import { getTimezoneOffset } from 'date-fns-tz';
import { SelectOption } from '@lib/components/Select/types';

/**
 * Gets a list of all available timezones with formatted labels
 * @returns Array of timezone options for Select component
 */
export default function getTimeZones(): SelectOption[] {
  // Get all available IANA timezone names
  // @ts-ignore - Intl.supportedValuesOf is available in modern browsers but TypeScript may not recognize it
  const timezones = Intl.supportedValuesOf('timeZone');

  const now = new Date();

  // Map timezones to SelectOption format with offset and name
  return timezones
    .map((timezone) => {
      try {
        // Get the UTC offset for this timezone
        const offset = getTimezoneOffset(timezone, now);
        const offsetHours = offset / (1000 * 60 * 60);
        const offsetMinutes = Math.abs(offset / (1000 * 60)) % 60;

        // Format offset as +/-HH:MM
        const sign = offsetHours >= 0 ? '+' : '-';
        const hours = Math.abs(Math.floor(offsetHours))
          .toString()
          .padStart(2, '0');
        const minutes = offsetMinutes.toString().padStart(2, '0');
        const offsetString = `${sign}${hours}:${minutes}`;

        // Format timezone name to be more readable
        const displayName = timezone.replace(/_/g, ' ');

        return {
          value: timezone,
          label: `(UTC${offsetString}) ${displayName}`,
        };
      } catch {
        // Fallback for any timezone that might cause an error
        return {
          value: timezone,
          label: timezone.replace(/_/g, ' '),
        };
      }
    })
    .sort((a, b) => {
      // Sort by UTC offset first, then by name
      const offsetA = getTimezoneOffset(a.value, now);
      const offsetB = getTimezoneOffset(b.value, now);

      if (offsetA !== offsetB) {
        return offsetA - offsetB;
      }

      return a.label.localeCompare(b.label);
    });
}

/**
 * Gets the user's current timezone
 * @returns SelectOption for the user's current timezone
 */
export function getUserTimeZone(): SelectOption | null {
  try {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezones = getTimeZones();
    return timezones.find((tz) => tz.value === userTimezone) || null;
  } catch {
    return null;
  }
}
